import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaChevronDown, FaTicketAlt } from 'react-icons/fa';
import useStore from '../../../store/useStore';

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

const CartCouponPanel = ({ subtotal, deliveryType, addressId, isAuthenticated, onRequireAuth, initialCode = '' }) => {
  const {
    cart_meta,
    available_coupons,
    coupons_loading,
    coupon_action_loading,
    fetchAvailableCoupons,
    applyCartCoupon,
    removeCartCoupon,
  } = useStore();
  const [code, setCode] = useState('');
  const [showCoupons, setShowCoupons] = useState(false);
  const applied = cart_meta?.applied_coupon;
  const appliedSavings = Number(cart_meta?.discount_amount ?? applied?.discount_amount ?? 0);
  const waivedDelivery = Number(applied?.delivery_fee_waived ?? 0);

  useEffect(() => {
    if (initialCode) setCode(String(initialCode).toUpperCase());
  }, [initialCode]);

  useEffect(() => {
    const selectedType = String(deliveryType || 'STANDARD').toUpperCase();
    const orderType = selectedType === 'EXPRESS' ? 'express_delivery' : selectedType === 'SCHEDULED' ? 'normal_delivery' : 'STANDARD';
    fetchAvailableCoupons({ placement: 'checkout', subtotal, orderType });
  }, [fetchAvailableCoupons, subtotal, deliveryType]);

  const apply = async (value = code) => {
    if (!isAuthenticated) {
      onRequireAuth?.();
      return;
    }
    const appliedOk = await applyCartCoupon(value, deliveryType, addressId);
    if (appliedOk) setCode('');
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-emerald-50 bg-[#f2faf5] px-4 py-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#046a38] shadow-sm"><FaTicketAlt /></span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-extrabold text-gray-900">Offers & coupons</h2>
          <p className="text-xs text-gray-600">Apply a code to see your updated total</p>
        </div>
      </div>

      <div className="p-4">
        {applied ? (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <FaCheckCircle className="shrink-0 text-lg text-[#07834b]" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-extrabold text-emerald-900">{applied.coupon_code || applied.code} applied</p>
              <p className="text-xs text-emerald-800">
                {applied.free_delivery || applied.discount_type === 'free_delivery'
                  ? `Free delivery applied${waivedDelivery > 0 ? ` · You saved ${money(waivedDelivery)} on delivery` : ''}`
                  : `${applied.coupon_name || 'Coupon savings'} · You saved ${money(appliedSavings)}`}
              </p>
            </div>
            <button type="button" disabled={coupon_action_loading} onClick={() => removeCartCoupon(deliveryType, addressId)} className="text-xs font-bold text-red-600 disabled:opacity-50">Remove</button>
          </div>
        ) : (
          <form onSubmit={(event) => { event.preventDefault(); apply(); }} className="flex gap-2">
            <input
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              aria-label="Coupon code"
              className="min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-semibold uppercase tracking-wide outline-none transition focus:border-[#07834b] focus:ring-2 focus:ring-emerald-100"
              maxLength={40}
            />
            <button type="submit" disabled={!code.trim() || coupon_action_loading || !subtotal} className="rounded-xl bg-[#046a38] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#03572e] disabled:cursor-not-allowed disabled:opacity-50">
              {coupon_action_loading ? 'Applying…' : 'Apply'}
            </button>
          </form>
        )}

        {available_coupons.length > 0 && (
          <div className="mt-3">
            <button type="button" onClick={() => setShowCoupons((open) => !open)} className="flex w-full items-center justify-between py-1 text-left text-xs font-bold text-[#046a38]">
              <span>{coupons_loading ? 'Updating offers…' : `See available offers (${available_coupons.length})`}</span>
              <FaChevronDown className={`transition-transform ${showCoupons ? 'rotate-180' : ''}`} />
            </button>
            {showCoupons && <div className="mt-2 space-y-2">
              {available_coupons.map((coupon) => (
                <div key={coupon.coupon_id ?? coupon.coupon_code} className="flex items-center gap-3 rounded-xl border border-dashed border-emerald-200 bg-white p-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-extrabold text-gray-900">{coupon.coupon_name || coupon.coupon_code}</p>
                    <p className="text-xs text-gray-600">{coupon.description || coupon.campaign_type}</p>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-[#046a38]">{coupon.coupon_code}</p>
                    {!coupon.is_applicable && coupon.inapplicable_reason && <p className="mt-1 text-xs text-amber-700">{coupon.inapplicable_reason}</p>}
                  </div>
                  <button type="button" disabled={!coupon.is_applicable || coupon_action_loading || !!applied} onClick={() => apply(coupon.coupon_code)} className="rounded-lg border border-[#046a38] px-3 py-1.5 text-xs font-extrabold text-[#046a38] disabled:border-gray-200 disabled:text-gray-400">
                    {applied ? 'Applied' : 'Apply'}
                  </button>
                </div>
              ))}
            </div>}
          </div>
        )}
        {!isAuthenticated && <p className="mt-2 text-[11px] text-gray-500">Sign in to apply a coupon.</p>}
      </div>
    </section>
  );
};

export default CartCouponPanel;
